

import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  IconButton,
} from "@mui/material";

import { useAuth } from "../../auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Pagination from "@mui/material/Pagination";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useLocation } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import logo from "../../assets/images/logo.png";
import AnimatedScoreRings from "../common/AnimatedScoreRings";
import MetroLineStepper from "../common/MetroLineScore";
import FilterListIcon from "@mui/icons-material/FilterList";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import { API_PATH } from "../../environments";


// 🔹 Helper for sorting
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const cmp = comparator(a[0], b[0]);
    if (cmp !== 0) return cmp;
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}


const LevelBadge = ({ level }) => {
  const colors = {
    Initial: "#C71313",
    Managed: "#C89600",
    Measured: "#008200",
    Optimize: "#0F62FE",
    Optimized: "#0F62FE", // safety for spelling
  };

  return (
    <Typography
      sx={{
        fontSize: "0.75rem",
        fontFamily: "Akkurat-Bold",
        color: colors[level] || "#000",
        whiteSpace: "nowrap",
        //textAlign: "center",
      }}
    >
      {level}
    </Typography>
  );
};

const GridFilterIcon = () => {
  const columns = 4;
  const rows = 5;
  const spacing = 4; // distance between centers
  const radius = 1.5;

  return (
    <svg
      width={columns * spacing}
      height={rows * spacing}
      viewBox={`0 0 ${columns * spacing} ${rows * spacing}`}
      fill="none"
    >
      {Array.from({ length: rows * columns }).map((_, i) => {
        const x = (i % columns) * spacing;
        const y = Math.floor(i / columns) * spacing;

        return (
          <circle
            key={i}
            cx={x + spacing / 2}
            cy={y + spacing / 2}
            r={radius}
            fill="#0036DD"
          />
        );
      })}
    </svg>
  );
};

const getDateRange = (filter) => {
  const now = new Date();
  let start = null;
  let end = new Date();

  switch (filter) {
    case "THIS_WEEK": {
      const today = new Date();
      const day = today.getDay();
      start = new Date(today);
      start.setDate(today.getDate() - day);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
      break;
    }
    case "LAST_WEEK": {
      const day = now.getDay() || 7;
      end = new Date(now);
      end.setDate(now.getDate() - day);
      start = new Date(end);
      start.setDate(end.getDate() - 6);
      break;
    }
    case "THIS_MONTH":
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case "LAST_MONTH":
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0);
      break;
    case "THIS_YEAR":
      start = new Date(now.getFullYear(), 0, 1);
      break;
    case "LAST_YEAR":
      start = new Date(now.getFullYear() - 1, 0, 1);
      end = new Date(now.getFullYear() - 1, 11, 31);
      break;
    default:
      return { start: null, end: null };
  }

  return { start, end };
};


const HistoryPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [searchText, setSearchText] = useState("");
  const [selected, setSelected] = useState([]);
  const location = useLocation();
  const [userDomain, setUserDomain] = useState("");


  const [hoveredRowId, setHoveredRowId] = useState(null);



  const completedDomain =
    user?.domain ||
    new URLSearchParams(location.search).get("domain") ||
    "SRE";


  const domains = ["DevOps", "SRE"];

  const otherDomain = domains.find(
    (d) => d.toLowerCase() !== completedDomain.toLowerCase()
  );

  //Filter logic

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [domainFilter, setDomainFilter] = useState(null);

  const [showDomainList, setShowDomainList] = useState(false);
  const openFilter = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const closeFilter = () => {
    setFilterAnchorEl(null);
  };

  const isFilterOpen = Boolean(filterAnchorEl);
  const [dateFilter, setDateFilter] = useState(null);
  // "THIS_WEEK" | "LAST_WEEK" | "THIS_MONTH" | "LAST_MONTH" | "THIS_YEAR" | "LAST_YEAR" | "CUSTOM" | null

  const [showDateList, setShowDateList] = useState(false);

  // Custom date range

  const [draftFromDate, setDraftFromDate] = useState(null);
  const [draftToDate, setDraftToDate] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectRowScore,setSelectedRowScore] = useState(0)
  const [selectedRowLevel,setSelectedRowLevel] = useState("")

  const parseRowDate = (dateStr) => {
    const d = new Date(dateStr);
    return isNaN(d) ? null : d;
  };

  const maxScore = 100;
  const stepScore = maxScore / 4;
  const completedSteps =  selectedRowLevel=="Optimized"?Math.floor(
    Math.round(selectRowScore || 0) / stepScore,
  )+1:Math.floor(
    Math.round(selectRowScore || 0) / stepScore,
  );
  useEffect(() => {
    setPage(1);
  }, [rowsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [searchText, domainFilter, dateFilter, fromDate, toDate]);


  useEffect(() => {
    if (user?.domain) {
      setUserDomain(user.domain);
    }
  }, [user]);
  useEffect(() => {
    if (location.state?.selectedRowId) {
      setSelected([location.state.selectedRowId]);
    }
  }, [location.state]);

  useEffect(() => {
    if (!user?.email || selected.length !== 1) return;

    const selectedRow = scores.find((r) => r.id === selected[0]);
    setSelectedRowScore(selectedRow.totalScore)
    setSelectedRowLevel(selectedRow?.level)
    if (!selectedRow) return;

    const evaluationId = selectedRow.evaluationId || selectedRow.id;

    // http://localhost:8085/api/evaluations/users/${encodeURIComponent
    const fetchUserDomain = async () => {
      try {
        const res = await fetch(
          `${API_PATH}/evaluations/users/${encodeURIComponent(
            user.email
          )}/category-raw-scores?evaluationId=${evaluationId}`,{credentials:'include'}
        );

        if (!res.ok) return;

        const data = await res.json();

        // ✅ THIS IS THE KEY LINE
        const latest = data[data.length - 1];
        setUserDomain(latest?.userDomain || "");
      } catch (e) {
        console.error("Failed to fetch userDomain", e);
      }
    };

    fetchUserDomain();
  }, [selected, scores, user]);



  const [resumeAvailable, setResumeAvailable] = useState(false);
  const [resumeDomain, setResumeDomain] = useState(null);
  const [resumeAvailableDevops,setResumeAvailableDevops] = useState(false)
  const [resumeAvailableSRE,setResumeAvailableSRE] = useState(false)



  useEffect(() => {
    async function checkResume(d) {
      try {
        // http://localhost:8085/api/evaluations/evaluation/progress?emailId=
        // for (const d of domains) {
          const res = await fetch(
            `${API_PATH}/evaluations/evaluation/progress?emailId=${encodeURIComponent(
              user.email
            )}&domain=${d}`,{
              credentials: 'include'
            }
          );

          const latestCompletedData = await fetch(
          `${API_PATH}/evaluations/latest-completed-answers?emailId=${encodeURIComponent(
            user?.email
          )}&domain=${encodeURIComponent(d)}`,{credentials: 'include'},
        );

          // if (!res.ok && !latestCompletedData.ok) ;
          const data = await res.json();
          const latestCompData =  await latestCompletedData.json()

          if (data?.resumeAvailable) {
            if (d==otherDomain)
              setResumeAvailableDevops(true)
             if (d==completedDomain)
              setResumeAvailableSRE(true)
          setResumeAvailable(true);
            setResumeDomain(d);
            data?.otherDomainsStatus[0]?.resumeAvailable ?
              localStorage.setItem("otherDomainResumeStatus",data?.otherDomainsStatus[0]?.domain) :localStorage.setItem("otherDomainResumeStatus",'') 
            data?.resumeAvailable ? 
              localStorage.setItem("resumeDomain",d) :localStorage.setItem("resumeDomain",'') 

            localStorage.setItem("resumeAvailable", "true");
            localStorage.setItem("resumeDomain", d);
            localStorage.setItem("resumeEvaluationId", data.evaluationId);
            // localStorage.setItem(`resumeCategoryIndex_${d.toLowerCase()}`, data.categoryIndex);
            localStorage.setItem("resumeCategoryIndex", data.categoryIndex);
            localStorage.setItem(`resumeLastCategory_${d.toLowerCase()}`, data.lastCategory);

            return; // ✅ stop once found
          }else{
            if (d==otherDomain)
              setResumeAvailableDevops(false)
              // localStorage.setItem("resumeLastCategoryDevops", "CICD & Automation");
            
             if (d==completedDomain)
              setResumeAvailableSRE(false)
              // localStorage.setItem("resumeLastCategorySRE", "Site Reliability Engineering");

          }
          if(latestCompData?.found){
             setResumeAvailable(true);
            setResumeDomain(d);
            data?.otherDomainsStatus[0]?.resumeAvailable ?
              localStorage.setItem("otherDomainResumeStatus",data?.otherDomainsStatus[0]?.domain) :localStorage.setItem("otherDomainResumeStatus",'') 
            data?.resumeAvailable ? 
              localStorage.setItem("resumeDomain",d) :localStorage.setItem("resumeDomain",'') 

            localStorage.setItem("resumeAvailable", "false");
            localStorage.setItem("resumeDomain", d);
            localStorage.setItem("resumeEvaluationId", latestCompData.evaluationId);
            localStorage.setItem("resumeCategoryIndex", latestCompData.categoryIndex);
            localStorage.setItem("resumeLastCategory", latestCompData.lastCategory);
            // localStorage.setItem(`resumeCategoryIndex_${d.toLowerCase()}`,  latestCompData.categoryIndex);
            // localStorage.setItem(`resumeLastCategory_${d.toLowerCase()}`, latestCompData.lastCategory);
            return; // ✅ stop once found
          }
          
        // }

        // No resume found
        setResumeAvailable(false);
        // localStorage.clear();

      } catch (e) {
        console.error(e);
      }
    }

    if (user?.email) {
      const domains = ["DevOps", "SRE"];
      for(const d of domains){
        checkResume(d);
      }
    }
  }, [user]);

  useEffect(() => {
    if (!user?.email) return;

    const fetchScores = async () => {
      try {
        const email = encodeURIComponent(user.email);
        // http://localhost:8085/api/evaluations/scores/by-email?emailId=${email}
        const response = await fetch(
          `${API_PATH}/evaluations/scores/by-email?emailId=${email}`,{
              credentials: 'include'
            }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch evaluation scores");
        }

        const data = await response.json();
        const indexedData = data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .map((item, index) => ({
            index: index + 1,
            ...item
          }));
        setScores(indexedData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchScores();
  }, [user]);

  // useEffect(() => {
  //   const savedRow = sessionStorage.getItem("selectedHistoryRow");
  //   if (savedRow) {
  //     setSelected([Number(savedRow)]);
  //   }
  // }, []);
useEffect(()=>{
  if (selected.length !=1)
    {
      setSelectedRowScore(0)
      setSelectedRowLevel("")
}

},[selected])
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // const filteredRows = scores.filter((row) =>
  //   Object.values(row).some((value) =>
  //     String(value).toLowerCase().includes(searchText.toLowerCase())
  //   )
  // );

  const startOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay() || 7; // Sunday fix
    d.setDate(d.getDate() - day + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const endOfWeek = (date) => {
    const d = startOfWeek(date);
    d.setDate(d.getDate() + 6);
    d.setHours(23, 59, 59, 999);
    return d;
  };

  const isWithinDateFilter = (createdAt, filter) => {
    if (!filter) return true;

    const rowDate = new Date(createdAt);
    const now = new Date();

    switch (filter) {
      case "THIS_WEEK":
        return (
          rowDate >= startOfWeek(now) &&
          rowDate <= endOfWeek(now)
        );

      case "LAST_WEEK": {
        const lastWeek = new Date();
        lastWeek.setDate(now.getDate() - 7);
        return (
          rowDate >= startOfWeek(lastWeek) &&
          rowDate <= endOfWeek(lastWeek)
        );
      }

      case "THIS_MONTH":
        return (
          rowDate.getMonth() === now.getMonth() &&
          rowDate.getFullYear() === now.getFullYear()
        );

      case "LAST_MONTH": {
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        return (
          rowDate.getMonth() === lastMonth.getMonth() &&
          rowDate.getFullYear() === lastMonth.getFullYear()
        );
      }

      case "THIS_YEAR":
        return rowDate.getFullYear() === now.getFullYear();

      case "LAST_YEAR":
        return rowDate.getFullYear() === now.getFullYear() - 1;



      default:
        return true;
    }
  };

  const filteredRows = scores
    // 🔎 Search
    .filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(searchText.toLowerCase())
      )
    )
    // 🌐 Domain
    .filter((row) =>
      domainFilter ? row.domain === domainFilter : true
    )
    // 📅 Date
    .filter((row) => {
      if (!dateFilter) return true;

      const rowDate = parseRowDate(row.createdAt);
      if (!rowDate) return false;

      // Custom range
      if (dateFilter === "CUSTOM") {
        if (!fromDate || !toDate) return true;

        return (
          rowDate >= new Date(fromDate) &&
          rowDate <= new Date(toDate)
        );
      }

      // Preset ranges
      const { start, end } = getDateRange(dateFilter);
      if (!start || !end) return true;

      return rowDate >= start && rowDate <= end;
    });



  const showNoResults =
    !loading &&
    filteredRows.length === 0 &&
    (searchText.trim() !== "" ||
      domainFilter !== null ||
      dateFilter !== null);



  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);

  const sortedRows = React.useMemo(() => {
    return stableSort(
      filteredRows,
      getComparator(order, orderBy)
    );
  }, [filteredRows, order, orderBy]);
  const lastCompletedSREId = sortedRows[sortedRows.findIndex(data => data.domain === "SRE")]?.evaluationId;
  const lastCompletedDevopsId = sortedRows[sortedRows.findIndex(data => data.domain === "DEVOPS")]?.evaluationId;
  localStorage.setItem("lastCompletedSREId", lastCompletedSREId);
  localStorage.setItem("lastCompletedDevOpsId", lastCompletedDevopsId);
  const visibleRows = React.useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);




  const headCells = [
    { id: "id", label: "ID" },
    { id: "createdAt", label: "Created On" },
    { id: "domain", label: "Domain" },
    { id: "level", label: "Level" },
    { id: "totalScore", label: "Total Score" },
    { id: "view", label: "View" },
  ];


  const handleViewDetails = (evaluationId, domain, totalScore, level, createdAt) => {

    //sessionStorage.setItem("selectedHistoryRow", selected[0]); // ✅ SAVE

    navigate(
      `/sre-score?evaluationId=${evaluationId}&domain=${encodeURIComponent(
        domain
      )}&email=${encodeURIComponent(user.email)}&name=${encodeURIComponent(
        user.userName
      )}`,
      {
        state: {
          createdAt, // ✅ pass date & time
        },
      }
    );
  };



  const primaryBtn = {
    display: "flex",
    height: "2.25rem",
    padding: "0.25rem 1rem",
    justifyContent: "center",
    alignItems: "center",
    gap: "0.25rem",
    width: { xs: "100%", sm: "11.25rem" },
    borderRadius: "1.75rem",
    background: "#0036DD",
    color: "#FFF",
    fontSize: "0.8rem",
    fontWeight: 500,
    textTransform: "none",
    whiteSpace: "nowrap",
    "&:hover": {
      background: "#00208F",
    },
  };


  const outlinedBtn = {
    display: "flex",
    height: "2.25rem",
    padding: "0.25rem 1rem",
    justifyContent: "center",
    alignItems: "center",
    gap: "0.25rem",
    width: { xs: "100%", sm: "11.25rem" },
    borderRadius: "1.75rem",
    background: "#FFF",
    border: "1px solid #00208F",
    color: "#00208F",
    fontSize: "0.8rem",
    fontWeight: 500,
    textTransform: "none",
    whiteSpace: "nowrap",
    "&:hover": {
      background: "#F2F6FF",
    },
  };


  React.useEffect(() => {
    setPage(1); // ✅ NOT 0
  }, [searchText]);

  const SortableHeader = ({ label, column }) => {
    const isActive = orderBy === column;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "0.25rem",
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => handleRequestSort(null, column)}
      >
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
          {label}
        </Typography>

        {/* 🔹 UP / DOWN arrows */}
        {label !== "View" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            lineHeight: 1,
          }}
        >
          <KeyboardArrowUpIcon
            sx={{
              fontSize: "1rem",
              color:
                isActive && order === "asc"
                  ? "#000"
                  : "#C0C7CF",
            }}
          />
          <KeyboardArrowDownIcon
            sx={{
              fontSize: "1rem",
              marginTop: "-4px", // 🔑 brings arrows closer
              color:
                isActive && order === "desc"
                  ? "#000"
                  : "#C0C7CF",
            }}
          />
        </Box>
      )}
      </Box>
    );
  };

  const getBase64ImageFromUrl = async (imageUrl) => {
    const response = await fetch(imageUrl,{credentials:'include'});
    const blob = await response.blob();

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  const getMaturityLevel = (score) => {
    if (score <= 50) return "Initial";
    if (score <= 75) return "Managed";
    if (score <= 89) return "Measured";
    return "Optimized";
  };

  const captureAnimatedRing = async () => {
    const element = document.querySelector("#history-pdf-ring-wrapper .history-pdf-ring-area");

    if (!element) {
      console.error("Ring wrapper not found");
      return null;
    }

    const canvas = await html2canvas(element, {
      backgroundColor: null,
      scale: 3,
      useCORS: true,
    });

    return canvas.toDataURL("image/png", 1.0);
  };

  const getImageSize = (base64) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.src = base64;
    });
  };


  const handleDownloadReport = async () => {
    let modules = ["Excellent", "Good", "Needs Improvement"];
    // if (selected.length !== 1) {
    //   alert("Please select exactly one assessment to download");
    //   return;
    // }
    const delay = (ms) => new Promise(res => setTimeout(res, ms));
    for (const selectedId of selected)
      {
    const selectedRow = scores.find((r) => r.id === selectedId);
    if (!selectedRow) return;

    const domain = selectedRow.domain;
    const evaluationId = selectedRow.evaluationId || selectedRow.id;

    try {
      const logoBase64 = await getBase64ImageFromUrl(logo);
// http://localhost:8085/api/evaluations/user/${encodeURIComponent
      const response = await fetch(
        `${API_PATH}/evaluations/user/${encodeURIComponent(
          user.email
        )}/domain/${domain}?evaluationId=${evaluationId}`,{credentials:'include'}
      );

      if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);

      const data = await response.json();
      const answers = data[data.length - 1]?.answers || [];

      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

      // ⭐ Capture the real animated ring
      const chartImage = await captureAnimatedRing();

      // ⭐ Detect actual width/height of PNG to prevent oval distortion
      const { width: imgW, height: imgH } = await getImageSize(chartImage);

      // Use minimum dimension to keep perfect circle
      const ringSizePx = Math.min(imgW, imgH);

      // PDF size for ring
      const renderSize = 40; // 40mm

      // Scale proportionally
      const scale = renderSize / ringSizePx;

      const renderWidth = imgW * scale;
      const renderHeight = imgH * scale;

      // Position
      const donutX = 15; // reduced from 150 to avoid overflow

      const donutSize = 45; // slightly larger ring but stays inside A4

      doc.addImage(logoBase64, "PNG", 14, 8, 40, 10);
      // Add image without distortion
      // doc.addImage(chartImage, "PNG", donutX, donutY, renderWidth, renderHeight);


   // ---------- HEADER ----------
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 54, 221);
      doc.setFontSize(16);
      doc.text(`Devops and SRE Maturity Compass `, 100, 18);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(14);
      // eslint-disable-next-line eqeqeq
      doc.text(
        `${domain} Maturity Assessment`,
        domain == "SRE" ? 135 : 125,
        28,
      );
         doc.setFont("helvetica", "normal");
      doc.setFontSize(12);

      if (selectedRow?.totalScore !== undefined) {
        const finalScore = Math.round(selectedRow?.totalScore);
        const maturityLevel = getMaturityLevel(finalScore);

        doc.setFillColor(230, 235, 245);
        doc.roundedRect(15, 30, 180, 40, 2, 2, "F");

        doc.setFontSize(12);
        doc.setTextColor(80);

        doc.text("Domain:", 17, 37);
        doc.setFont(undefined, "bold");
        doc.text(userDomain || "--", 37, 37);
        doc.setFont(undefined, "normal");
        doc.text("|", 67, 37);
        doc.text("Maturity Score:", 70, 37);
        doc.setFont(undefined, "bold");
        doc.text(`${finalScore}%`, 100, 37);
        doc.setFont(undefined, "normal");
        doc.text("|", 115, 37);
        doc.text("Maturity Level:", 117, 37);
        doc.setFont(undefined, "bold");
        doc.text(maturityLevel, 150, 37);
      }

      // ---------- MATURITY LEVEL BOXES ----------
       doc.setFillColor(255, 255, 255);
      doc.roundedRect(20, 45, 170, 22, 2, 2, "F");
      doc.setTextColor(0, 0, 0);
      doc.addImage(chartImage, "PNG", donutX, 47, 120, 10);
      doc.setFontSize(10);
      doc.setFont("helvetica", completedSteps === 1 ? "bold" : "normal");
      doc.text("Initial", 47, 64);
      doc.setFont("helvetica", completedSteps === 2 ? "bold" : "normal");
      doc.text("Managed", 65, 64);
      doc.setFont("helvetica", completedSteps === 3 ? "bold" : "normal");
      doc.text("Measured", 90, 64);
      doc.setFont("helvetica", completedSteps === 4 ? "bold" : "normal");
      doc.text("Optimized", 115, 64);

      // const tableStartY = boxY + boxHeight + 8;

      // ---------- NORMALIZE ----------
      const normalize = (remark) => {
        if (!remark) return "";
        const r = remark.toLowerCase();
        if (r.includes("excellent")) return "Excellent";
        if (r.includes("good")) return "Good";
        // if (r.includes("basic")) return "Basic";
        if (r.includes("needs")) return "Needs Improvement";
        return "";
      };

      const format = (items, type) =>
        [
          ...new Set(
            items
              .filter((i) => normalize(i.remarks) === type)
              .map((i) => i.recommendation)
              .filter(Boolean)
          ),
        ]
          .map((t) => `• ${t}`)
          .join("\n") || "-";

      // ---------- TABLE ----------
      const tableData = [];
      const categories = [...new Set(answers.map((a) => a.category))];
      categories.forEach((cat) => {
        const items =answers?.filter((i) => i.category === cat);

        const allRecs = [
          ...new Set(items.map((i) => i.recommendation?.trim()).filter(Boolean)),
        ];

        const limited = items.filter((i) =>
          allRecs.slice(0, 5).includes(i.recommendation?.trim())
        );

        tableData.push([
          cat,
          format(limited, "Excellent"),
          format(limited, "Good"),
          format(limited, "Needs Improvement"),
        ]);
      });
      doc.setFillColor(230, 235, 245);
      // doc.setFontSize(12);
      // doc.roundedRect(15, 73, 25, 7, 1, 1, "F");
      // doc.setFont("Akkurat-Light", "Normal");
      // doc.text("Module", 20, 78);
      // doc.setFont("helvetica", "normal");

      let y = 88;

const descBoxes = [
  {
    title: "Initial",
    color: [199, 19, 19],
    text: "The environment lacks alignment with best practices, \r\nresulting in manual bottlenecks and high operational risk.",
  },
  {
    title: "Managed",
    color: [251, 188, 5],
    text: "Core best practices are present, but execution is inconsistent and \r\nrequires structural maturity to scale.",
  },
  {
    title: "Measured",
    color: [0, 130, 0],
    text: "Industry best practices are integrated with robust tooling and \r\ntelemetry, enabling objective performance tracking.",
  },
  {
    title: "Optimized",
    color: [15, 98, 254],
    text: "The system operates with peak efficiency, leveraging advanced \r\nmethodologies and continuous security compliance.",
  },
];

let topBoxX = 14;
let topBoxY = y;
const topBoxWidth = 90;
const topBoxHeight = 14;

descBoxes.forEach((b, index) => {
  if (index === 2) {
    topBoxX = 14;
    topBoxY += topBoxHeight + 4;
  }

  doc.setDrawColor(255, 255, 255);
  doc.rect(topBoxX, topBoxY, topBoxWidth, topBoxHeight);

  doc.setFillColor(245, 246, 248);
  doc.rect(topBoxX, topBoxY, 20, 5, "F");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...b.color);
  doc.text(b.title, topBoxX + 2, topBoxY + 3.5);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  doc.text(b.text, topBoxX + 2, topBoxY + 9);

  topBoxX += topBoxWidth + 2;
});

// ✅ IMPORTANT
// move y below desc boxes
y = topBoxY + topBoxHeight + 12;

/////////////////////////////////////////////////////////////////////////////////////////////
// MODULE HEADER

      doc.setFillColor(230, 235, 245);
      doc.setFontSize(12);
      doc.roundedRect(15, y - 2, 25, 7, 1, 1, "F");

      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text("Module", 20, y + 3);

      doc.setFont("helvetica", "normal");
      // table starts AFTER module title
      y += 12;
      const pageHeight = doc.internal.pageSize.getHeight();

      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const usableWidth = pageWidth - margin * 2;
      const lineHeight = 7;
      console.log("tableData=>", tableData);
      let showmodule = true;

      tableData.map((paragraphs, index) => {
        const count = paragraphs.filter(item => item === '-').length;
        if(count<=2)
        paragraphs.map((p, index) => {
          const lines = doc.splitTextToSize(p, usableWidth);
          showmodule = true;

          lines.map((line, i) => {
            if (index == 0) {
              doc.setFillColor(230, 235, 245);
              doc.setFontSize(12);

              doc.roundedRect(15, y - 5, 180, 7, 1, 1, "F");
              doc.setFont("helvetica", "bold");
              doc.text(line, margin, y);
              doc.setFont("helvetica", "normal");
              y += lineHeight;
              // eslint-disable-next-line no-const-assign
              // showmodule = true;
            } else {
              if (showmodule) {
                doc.setFontSize(12);

                doc.setFont("helvetica", "bold");
                doc.text(modules[index - 1], margin, y);
                doc.setFont("helvetica", "normal");
                y += lineHeight;
                showmodule = false;
              }

              // if (y + lineHeight > pageHeight - margin) {
              //   doc.addPage();
              //   y = margin;
              // }
              const footerHeight = 12;
              if (y + lineHeight > pageHeight - margin - footerHeight) {
                doc.addPage();
                y = margin;


              }
              doc.setFontSize(10);

              doc.text(line !== "-" ? line : "• NA", margin, y);
              y += lineHeight;
 
            }
          });
          y += 5;
        });
          doc.setFontSize(9);
          doc.setTextColor(119, 128, 136)
          doc.text("Generated by Sandhata Technologies", 14, pageHeight - 10);

          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const dateString = new Date().toLocaleString("en-IN", {
            timeZone: tz,
            dateStyle: "medium",
            timeStyle: "short",
          });

          doc.text(
            `Generated on: ${dateString} (${tz})`,
            pageWidth - 14,
            pageHeight - 10,
            { align: "right" }
          );
          doc.setTextColor(0,0,0)
          doc.text(
            `Page ${doc.internal.getNumberOfPages()}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: "center" }
          );
             
      });
      //  const descBoxes = [
      //   {
      //     title: "Initial",
      //     color: [199, 19, 19],
      //     text: "The environment lacks alignment with DevSecOps best practices, \r\nresulting in manual bottlenecks and high operational risk.",
      //   },
      //   {
      //     title: "Managed",
      //     color: [251, 188, 5],
      //     text: "Core best practices are present, but execution is inconsistent and \r\nrequires structural maturity to scale.",
      //   },
      //   {
      //     title: "Measured",
      //     color: [0, 130, 0],
      //     text: "Industry best practices are integrated with robust tooling and \r\ntelemetry, enabling objective performance tracking.",
      //   },
      //   {
      //     title: "Optimize",
      //     color: [15, 98, 254],
      //     text: "The system operates with peak efficiency, leveraging advanced \r\nDevOps methodologies and continuous security compliance.",
      //   },
      // ];

      // let boxX = 14;
      // let boxY = y + 10;
      // const boxWidth = 90;
      // const boxHeight = 14;
      //  if((boxY+100) >275){
      //   boxY=20
      //   doc.addPage()
      //    doc.setFontSize(9);
      //     doc.setTextColor(119, 128, 136)
      //     doc.text("Generated by Sandhata Technologies", 14, pageHeight - 10);

      //     const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      //     const dateString = new Date().toLocaleString("en-IN", {
      //       timeZone: tz,
      //       dateStyle: "medium",
      //       timeStyle: "short",
      //     });

      //     doc.text(
      //       `Generated on: ${dateString} (${tz})`,
      //       pageWidth - 14,
      //       pageHeight - 10,
      //       { align: "right" }
      //     );
      //     doc.setTextColor(0,0,0)
      //     doc.text(
      //       `Page ${doc.internal.getNumberOfPages()}`,
      //       pageWidth / 2,
      //       pageHeight - 10,
      //       { align: "center" }
      //     );
      // }
      // descBoxes.forEach((b, index) => {
      //   if (index === 2) {
      //     boxX = 14;
      //     boxY += boxHeight + 3;
      //   }

      //   doc.setDrawColor(255,255,255);
      //   doc.rect(boxX, boxY, boxWidth, boxHeight);

      //   doc.setFillColor(245, 246, 248);
      //   doc.rect(boxX, boxY, 20, 5, "F");

      //   doc.setFont("helvetica", "normal");
      //   doc.setFontSize(9);
      //   doc.setTextColor(...b.color);
      //   doc.text(b.title, boxX + 2, boxY + 3.5);

      //   doc.setFont("helvetica", "normal");
      //   doc.setFontSize(8);
      //   doc.setTextColor(0, 0, 0);
      //   doc.text(b.text, boxX + 2, boxY + 9);

      //   boxX += boxWidth + 2;
      // });

      // ---------- DISCLAIMER ----------
      const disclaimerText =
      "This DevOps and SRE Maturity Report and its associated recommendations are generated automatically based solely on the data "+
      "and responses provided by the participant. Sandhata does not independently verify the accuracy, completeness, or truthfulness of the information submitted. "+
      "The insights provided are for informational and educational purposes only and do not constitute professional consulting, engineering, or legal advice. "+
      "Sandhata accepts no liability for any business decisions, financial losses, or operational outcomes resulting from the use of or reliance upon this report. "+
      "We strongly recommend proper consultation before making any changes."
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      const disclaimerLines = doc.splitTextToSize(disclaimerText, usableWidth);

      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, pageWidth - margin, y);
      y += 5;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      doc.text("Disclaimer : ", margin, y);
      y += 5;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(100, 100, 100);

      // Fit all lines in remaining space above footer — no new page
      const availableSpace = pageHeight - 15 - y;
      const neededSpace = disclaimerLines.length * 5;
      const lineSpacing = neededSpace > availableSpace
        ? Math.max(3.5, availableSpace / disclaimerLines.length)
        : 5;
      disclaimerLines.forEach((line,i ) => {
        doc.text(line, i==0?margin+5:margin, y);
        y += lineSpacing;
      });
      doc.setTextColor(0, 0, 0);

      doc.save(`${domain} Maturity Assessment.pdf`);
      await delay(1000)
    } catch (err) {
      console.error(err);
      alert("Failed to download report");
    }
  }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "flex-start", // 🔑 IMPORTANT
        mt: "8px",                        // space below navbar
        px: 1.5,                      // spacing from sidebar
        boxSizing: "border-box",
      }}
    >



      <Box
        sx={{
          width: "100%",      // 🔑 full content width
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >

        {/* ================= ACTION BAR ================= */}
        <Box
          sx={{
            display: "flex",
            width: "100%",
            padding: "0.5rem 1rem",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            borderRadius: "1rem",
            background: "#FFF",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            flexWrap: { xs: "wrap", md: "nowrap" },
            gap: 1,
            boxSizing: "border-box",
          }}
        >

          {/* LEFT BUTTONS */}
          <Box
            sx={{
              display: "flex",
              gap: "0.5rem",
              flex: 1,
              flexWrap: "wrap",
            }}
          >
            <Button
              sx={primaryBtn}
              onClick={() =>
                navigate(`/questions/${otherDomain.toLowerCase()}`)
              }
            >
              {/* {continueDomain == otherDomain ? `Resume ${otherDomain} Assessment`:`Start ${otherDomain} Assessment`} */}      
              {resumeAvailableDevops ? `Resume ${otherDomain} Assessment`:`Start ${otherDomain} Assessment`}
            </Button>


            <Button
              sx={outlinedBtn}
              onClick={() =>
                navigate(`/questions/${completedDomain.toLowerCase()}`)
              }
            >
              {/* Start {completedDomain} Assessment */}
              {/* {completedDomain == otherDomainResumeStatus ? `Resume ${completedDomain} Assessment`:`Start ${completedDomain} Assessment`} */}
              {resumeAvailableSRE ? `Resume ${completedDomain} Assessment`:`Start ${completedDomain} Assessment`}

            </Button>


            {/* <Button
              sx={{
                ...outlinedBtn,
                opacity: resumeAvailable ? 1 : 0.5,
                cursor: resumeAvailable ? "pointer" : "not-allowed",
              }}
              disabled={!resumeAvailable}
              onClick={() => {
                if (!resumeAvailable) return;

                const domain =
                  resumeDomain || localStorage.getItem("resumeDomain");

                const evaluationId =
                  localStorage.getItem("resumeEvaluationId");

                const categoryIndex =
                  Number(localStorage.getItem("resumeCategoryIndex"));

                navigate(`/questions/${domain.toLowerCase()}`, {
                  state: {
                    resume: true,
                    evaluationId,
                    categoryIndex,
                  },
                });
              }}
            >
              Continue Assessment
            </Button> */}



          </Box>


          {/* <Tooltip
            title={
              selected.length !== 1
                ? "Please select one evaluation to download the report"
                : ""
            }
            arrow
            disableHoverListener={selected.length === 1}
          > */}
            <Button
              sx={{
                ...primaryBtn,
                width: { xs: "100%", sm: "11.25rem" },
                backgroundColor: "#0036DD",
                "&.Mui-disabled": {
                  backgroundColor: "transparent",
                  border: "1px solid #00208F",
                  color: "#00208F",
                  opacity: 0.5,
                  cursor: "not-allowed",
                },
                "&:hover": {
                  backgroundColor: "#00208F",
                },
              }}
              disabled={selected.length == 0}
              onClick={handleDownloadReport}
            >
              Download
            </Button>
          {/* </Tooltip> */}



        </Box>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            height: { xs: "calc(100dvh - 180px)", md: "500px" },
            overflowY: "auto",
          }}
        >
          {/* Search + Table goes here */}
          {/* <TableContainer
            component={Paper}
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              maxWidth: "64.875rem",
              mx: "auto",
              borderRadius: "1.25rem",
              background: "#FFF",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              overflowY: "auto",
              overflowX: "hidden",
              maxHeight: "75vh",
            }}
          > */}

          <TableContainer
            component={Paper}
            sx={{
              display: "flex",
              flexDirection: "column",

              width: "100%",
              mx: "auto",

              borderRadius: "1.25rem",
              background: "#FFF",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",

              overflowY: "visible",
              overflowX: "auto",
            }}
          >


            {/* 🔹 STICKY TITLE + SEARCH (PASTE HERE) */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 4,
                display: "flex",
                flexWrap: { xs: "wrap", sm: "nowrap" },
                minHeight: "2.75rem",
                px: 2,
                py: { xs: "0.5rem", sm: 0 },
                alignItems: "center",
                justifyContent: "space-between",
                gap: { xs: "0.5rem", sm: 0 },
                background: "#FFF",
                borderBottom: "1px solid #E0E0E0",
              }}
            >

              <Typography
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  fontFamily: "Akkurat-Bold",
                  whiteSpace: "nowrap",
                }}
              >
                Assessment History
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", flex: 1, justifyContent: "flex-end" }}>
                {/* Search */}
                <TextField
                  size="small"
                  placeholder="Search"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  sx={{
                    width: { xs: "100%", sm: "18rem", md: "24rem", lg: "33rem" },
                    "& .MuiOutlinedInput-root": {
                      height: "1.75rem",
                      borderRadius: "0.5rem",
                      fontSize: "0.8rem",
                    },
                    "& input": {
                      padding: "0 0.5rem",
                    },
                  }}
                  InputProps={{
                    endAdornment: <SearchIcon sx={{ fontSize: 18, color: "#0036DD" }} />,
                  }}
                />

                {/* 🔲 Grid-style filter icon (as per 2nd image) */}
                <Box
                  sx={{
                    width: "1.9rem",
                    height: "1.9rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "0.5rem",
                    // border: "1px solid #E0E0E0",
                    cursor: "pointer",
                    //"&:hover": { backgroundColor: "#F5F6F8" },
                  }}
                  onClick={openFilter}
                >
                  <GridFilterIcon />
                </Box>
              </Box>
            </Box>
            <Table
              stickyHeader
              sx={{
                
                "& .MuiTableRow-root": {
                  height: "2rem",
                },
              
                "& .MuiTableCell-root": {
                  padding: "0.5rem",
                  height: "2rem",
                  fontSize: "0.75rem",
                  whiteSpace: "nowrap",
                },
              
                "& .MuiTableCell-paddingCheckbox": {
                  padding: "0 0.25rem",
                  width: "2rem",
                },
              
                /* ✅ CHECKBOX SIZE + COLOR */
                "& .MuiCheckbox-root": {
                  padding: 0,
                  width: "1.125rem",
                  height: "1.125rem",
              
                  "& .MuiSvgIcon-root": {
                    width: "1.125rem",
                    height: "1.125rem",
                    fontSize: "1.125rem",     // 🔑 controls actual checkbox size
                    color: "#602DEA",         // unchecked border
                  },
              
                  "&.Mui-checked .MuiSvgIcon-root": {
                    color: "#602DEA",         // checked fill
                  },
                },
              }}
              
              
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    sx={{
                      position: "sticky",
                      top: "2.75rem",
                      zIndex: 3,

                      backgroundColor: "#F5F6F8",
                      height: "2rem",

                    }}
                  >
                    <Box sx={{ pl: 1 }}>
                      <Checkbox size="medium"                           sx={{
                        transform: "scale(1.2)",   // ⬅️ increase size
                      }} />
                    </Box>
                  </TableCell>

                  {headCells.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      sx={{
                        position: "sticky",
                        top: "2.75rem",
                        zIndex: 3,
                        backgroundColor: "#F5F6F8",
                        height: "2rem",
                        padding: "0 0.5rem",
                        whiteSpace: "nowrap",
                        textAlign: "center",     // 🔑 IMPORTANT
                        width: "6rem",           // 🔑 LOCK WIDTH
                        minWidth: "6rem",
                        maxWidth: "6rem",
                      }}
                    >
                      <SortableHeader label={headCell.label} column={headCell.id} />
                    </TableCell>

                  ))}
                </TableRow>
              </TableHead>



              <TableBody>
                 {visibleRows.length == 0 && <TableRow>
                  <TableCell colSpan={headCells.length + 1}>
                    <Box
                      sx={{
                        py: 4,
                        textAlign: "center",
                        color: "#6F6F6F",
                      }}
                    >
                      <Typography sx={{ fontSize: "0.9rem", fontWeight: 600,color:'black' }}>
                        No records found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>  }
                {showNoResults ? (
                  <TableRow>
                    <TableCell colSpan={headCells.length + 1}>
                      <Box
                        sx={{
                          py: 4,
                          textAlign: "center",
                          color: "#6F6F6F",
                        }}
                      >
                        <Typography sx={{ fontSize: "0.9rem", fontWeight: 500 }}>
                          No matching results found
                        </Typography>



                        {(
                          searchText ||
                          domainFilter ||
                          dateFilter ||
                          fromDate ||
                          toDate
                        ) && (
                            <Button
                              size="small"
                              sx={{ mt: 1, textTransform: "none" }}
                              onClick={() => {
                                setSearchText("");
                                setDomainFilter(null);
                                setDateFilter(null);
                                setFromDate(null);
                                setToDate(null);
                                setDraftFromDate("");
                                setDraftToDate("");

                              }}
                            >
                              Clear filters
                            </Button>
                          )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleRows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell padding="checkbox">
                        <Box sx={{pl:1}}>
                        <Checkbox
                          size="small"
                           sx={{transform: "scale(1.2)",   // ⬅️ increase size
                            }}
                          checked={selected.includes(row.id)}
                          onChange={() =>
                            setSelected((prev) =>
                              prev.includes(row.id)
                                ? prev.filter((id) => id !== row.id)
                                : [...prev, row.id]
                            )
                          }
                        />
                        </Box>
                      </TableCell>

                      <TableCell
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "0.5rem",
                        }}
                      >
                        <Typography sx={{ fontSize: "0.75rem" }}>
                          {row.index}
                        </Typography>

                        {/* {selected.includes(row.id) && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(
                                row.evaluationId || row.id,
                                row.domain,
                                row.totalScore,
                                row.level,
                                row.createdAt
                              );
                            }}
                            sx={{ padding: "2px" }}
                          >
                            <VisibilityIcon sx={{ fontSize: "1rem", color: "#0036DD" }} />
                          </IconButton>
                        )} */}
                      </TableCell>

                      <TableCell>{row.createdAt}</TableCell>
                      <TableCell>{row.domain}</TableCell>

                      <TableCell sx={{ minWidth: "6rem", maxWidth: "6rem" }}>
                        <LevelBadge level={row.level} />
                      </TableCell>

                      <TableCell>
                        {Number(row.totalScore).toFixed(2)}%
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(
                              row.evaluationId || row.id,
                              row.domain,
                              row.totalScore,
                              row.level,
                              row.createdAt,
                            );
                          }}
                          sx={{ padding: "2px" }}
                        >
                          <VisibilityIcon
                            sx={{ fontSize: "1rem", color: "#000000" }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>


            </Table>

            {/* Desktop: pagination centered, items-per-page on right */}
            <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", px: 2, py: 1 }}>
              <Box sx={{ flex: 1 }} />
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                siblingCount={1}
                boundaryCount={1}
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": { fontSize: "0.75rem", height: "1.75rem", minWidth: "1.75rem", borderRadius: "50%" },
                  "& .Mui-selected": { backgroundColor: "#E0E7FF", color: "#0036DD", fontWeight: 600 },
                }}
              />
              <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: "0.75rem" }}>Items per page:</Typography>
                <TextField select size="small" value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))}
                  sx={{ width: "4.5rem", "& .MuiOutlinedInput-root": { height: "1.75rem", fontSize: "0.75rem" } }}
                  SelectProps={{ native: true }}
                >
                  {[6, 10, 25].map((o) => <option key={o} value={o}>{o}</option>)}
                </TextField>
              </Box>
            </Box>


          </TableContainer>

          {/* Mobile: sticky pagination fixed at bottom of scroll container */}
          <Box
            sx={{
              display: { xs: "flex", sm: "none" },
              position: "sticky",
              bottom: 0,
              zIndex: 5,
              flexDirection: "column",
              alignItems: "center",
              px: 2,
              py: 1,
              gap: 1,
              backgroundColor: "#FFF",
              borderTop: "1px solid #E0E0E0",
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              siblingCount={1}
              boundaryCount={1}
              size="small"
              sx={{
                "& .MuiPaginationItem-root": { fontSize: "0.75rem", height: "1.75rem", minWidth: "1.75rem", borderRadius: "50%" },
                "& .Mui-selected": { backgroundColor: "#E0E7FF", color: "#0036DD", fontWeight: 600 },
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: "0.75rem" }}>Items per page:</Typography>
              <TextField select size="small" value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))}
                sx={{ width: "4.5rem", "& .MuiOutlinedInput-root": { height: "1.75rem", fontSize: "0.75rem" } }}
                SelectProps={{ native: true }}
              >
                {[6, 10, 25].map((o) => <option key={o} value={o}>{o}</option>)}
              </TextField>
            </Box>
          </Box>

        </Box>
      </Box>

      {/* 🔒 Hidden ring for PDF capture */}
      <Box
        sx={{
          position: "absolute",
          left: "-9999px",
          top: 0,
        }}
      >
        <Box
                sx={{
                  padding: "1.5rem",
                  borderRadius: "1rem",
                  border: "1px solid #C0C7CF",
                  background: "#FFF",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
        <div id="history-pdf-ring-wrapper" style={{ width: "500px" }}>
          <div className="history-pdf-ring-area" style={{ width: "500px", overflowX: "hidden" }}>
            <MetroLineStepper
              activeStep={3}
              score={Math.round(selectRowScore || 0)}
              maturityLevel={selectedRowLevel}
              mt={0}
              showLabels={false}
              connectorWidth="68px"
              circleSize={20}
              circleHeight={12}
            />
          </div>
        </div>
        </Box>

        <Popover
          open={isFilterOpen}
          anchorEl={filterAnchorEl}
          onClose={closeFilter}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              width: "16rem",
              borderRadius: "1rem",
              p: 1,
            },
          }}
        >
          {/* DOMAINS SECTION */}
          <Box
            onMouseEnter={() => setShowDomainList(true)}
            onMouseLeave={() => setShowDomainList(false)}
          >
            <Typography
              sx={{
                px: 1,
                py: 0.75,
                fontSize: "0.9rem",
                fontWeight: 600,
              }}
            >
              Domains
            </Typography>

            {showDomainList && (
              <Box sx={{ pl: 2 }}>
                {["DevOps", "SRE"].map((domain) => (
                  <Box
                    key={domain}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      py: "0.4rem",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setDomainFilter(domain.toUpperCase());
                      closeFilter();
                    }}
                  >
                    <Box
                      sx={{
                        width: "0.4rem",
                        height: "0.4rem",
                        borderRadius: "50%",
                        backgroundColor:
                          domainFilter === domain ? "#0036DD" : "#C0C7CF",
                      }}
                    />
                    <Typography sx={{ fontSize: "0.8rem" }}>
                      {domain}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* DIVIDER */}
          <Box sx={{ my: 1, borderBottom: "1px solid #E0E0E0" }} />

          {/* DATE SECTION */}
          <Box
            onMouseEnter={() => setShowDateList(true)}
            onMouseLeave={() => setShowDateList(false)}
          >
            <Typography
              sx={{
                px: 1,
                py: 0.75,
                fontSize: "0.9rem",
                fontWeight: 600,
              }}
            >
              Date
            </Typography>

            {showDateList && (
              <Box sx={{ pl: 2 }}>
                {[
                  { label: "This Week", value: "THIS_WEEK" },
                  { label: "Last Week", value: "LAST_WEEK" },
                  { label: "This Month", value: "THIS_MONTH" },
                  { label: "Last Month", value: "LAST_MONTH" },
                  { label: "This Year", value: "THIS_YEAR" },
                  { label: "Last Year", value: "LAST_YEAR" },
                  { label: "Custom range", value: "CUSTOM" },
                ].map((opt) => (
                  <Box
                    key={opt.value}
                    sx={{ display: "flex", alignItems: "center", gap: 1, py: "0.4rem", cursor: "pointer" }}
                    onClick={() => {
                      setDateFilter(opt.value);
                      if (opt.value !== "CUSTOM") closeFilter();
                    }}
                  >
                    <Box
                      sx={{
                        width: "0.4rem",
                        height: "0.4rem",
                        borderRadius: "50%",
                        backgroundColor: dateFilter === opt.value ? "#0036DD" : "#C0C7CF",
                      }}
                    />
                    <Typography sx={{ fontSize: "0.8rem" }}>{opt.label}</Typography>
                  </Box>
                ))}

                {/* ✅ SHOW ONLY WHEN CUSTOM */}
                {dateFilter === "CUSTOM" && (
                  <Box
                    sx={{ mt: 1, pr: 2 }}
                    onClick={(e) => e.stopPropagation()} // 🔑 keeps popover open
                  >
                    <TextField
                      type="date"
                      label="From"
                      size="small"
                      value={draftFromDate}
                      onChange={(e) => setDraftFromDate(e.target.value)}
                      sx={{ width: "100%", mb: 1 }}
                      InputLabelProps={{ shrink: true }}
                    />

                    <TextField
                      type="date"
                      label="To"
                      size="small"
                      value={draftToDate}
                      onChange={(e) => setDraftToDate(e.target.value)}
                      sx={{ width: "100%" }}
                      InputLabelProps={{ shrink: true }}
                    />

                    <Button
                      size="small"
                      sx={{ mt: 1, textTransform: "none" }}
                      disabled={!draftFromDate || !draftToDate}
                      onClick={() => {
                        setFromDate(new Date(draftFromDate+"T00:00:00"));
                        setToDate(new Date(draftToDate+"T23:59:59"));
                        closeFilter();
                      }}
                    >
                      Apply
                    </Button>
                  </Box>
                )}
              </Box>
            )}


          </Box>
          <Box sx={{ my: 1, borderBottom: "1px solid #E0E0E0" }} />
             <Button
                sx={{color:'black',fontWeight:800 }}
                onClick={() => {
                closeFilter();
                setSearchText("");
                setDomainFilter(null);
                setDateFilter(null);
                setFromDate(null);
                setToDate(null);
                setDraftFromDate("");
                setDraftToDate("");
              }}
                >
              Reset Filter
              </Button>
        </Popover>


      </Box>
    
    </Box>

  );

};

export default HistoryPage;
